// Models
import {
  MetricMappingMethod,
  NumericComparisonOperator,
  RiskMetricType,
} from 'app/modules/riskRatings/models';
import { RiskParameterName } from 'app/modules/riskRatings/components/riskParameters';
import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import { InvestigationStatus } from 'app/modules/investigations/models';
import { AlertType } from 'app/modules/alerts/types';
import { RuleStatuses } from 'app/modules/rules/models';
import { PaginationPayload } from 'app/shared/pagination/models';
import {
  AgeOfRecordMetric,
  ArticleVolumeMetric,
  EntityInformationMatchMetric,
  GeographicMatchMetric,
  SarVolumeMetric,
} from 'app/modules/riskRatings/components/riskParameters/metrics';
import { EMPTY_RULE_CONDITION_GROUP } from 'app/modules/detectionModels/constants';
import { EntityRiskSegmentResponse } from 'app/modules/riskRatings/responses';

export const MIN_RISK = 0;
export const MAX_RISK = 100;
export const DEFAULT_LOW_RISK_CEILING = 40;
export const DEFAULT_HIGH_RISK_FLOOR = 80;

export const RISK_SLIDER_MARKS = [
  {
    label: '0',
    value: MIN_RISK,
  },
  {
    label: '50',
    value: MAX_RISK / 2,
  },
  {
    label: '100',
    value: MAX_RISK,
  },
];

export const DEFAULT_ENTITY_RISK_SEGMENTS: Omit<
  EntityRiskSegmentResponse,
  'id'
>[] = [
  {
    title: '',
    filters: EMPTY_RULE_CONDITION_GROUP,
    parameters: [],
  },
];

export const U21_ENTITY_RISK_SEGMENT_SIMPLE_MODEL: EntityRiskSegmentResponse[] =
  [
    {
      ...DEFAULT_ENTITY_RISK_SEGMENTS[0],
      parameters: [
        {
          parameter: RiskParameterName.GEOGRAPHIC_MATCH,
          weight: 0.5,
          parameter_args: {},
          metrics: [
            {
              metric: GeographicMatchMetric.ENTITY_COUNTRY,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 100,
              unscored_risk: 0,
              risk_mappings: [],
            },
            {
              metric: GeographicMatchMetric.ENTITY_STATE,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 100,
              unscored_risk: 0,
              risk_mappings: [],
            },
            {
              metric: GeographicMatchMetric.ENTITY_CITY,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 100,
              unscored_risk: 0,
              risk_mappings: [],
            },
            {
              metric: GeographicMatchMetric.ENTITY_ZIP_CODE,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 100,
              unscored_risk: 0,
              risk_mappings: [],
            },
          ],
        },
        {
          parameter: RiskParameterName.AGE_OF_RECORD,
          weight: 0.5,
          parameter_args: {},
          metrics: [
            {
              metric: AgeOfRecordMetric.ENTITY_MONTHS_FROM_REGISTRATION,
              metric_type: RiskMetricType.NUMBER,
              unknown_risk: 100,
              unscored_risk: 0,
              risk_mappings: [
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  risk: 100,
                  content: {
                    operator: NumericComparisonOperator.LT,
                    value: 3,
                  },
                },
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  risk: 80,
                  content: {
                    operator: NumericComparisonOperator.LT,
                    value: 6,
                  },
                },
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  risk: 60,
                  content: {
                    operator: NumericComparisonOperator.LT,
                    value: 9,
                  },
                },
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  risk: 40,
                  content: {
                    operator: NumericComparisonOperator.LT,
                    value: 12,
                  },
                },
              ],
            },
          ],
        },
        {
          parameter: RiskParameterName.ENTITY_INFORMATION_MATCH,
          weight: 0.5,
          parameter_args: {},
          metrics: [
            {
              metric: EntityInformationMatchMetric.ENTITY_BUSINESS_NAME,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [],
            },
            {
              metric: EntityInformationMatchMetric.ENTITY_AGE,
              metric_type: RiskMetricType.NUMBER,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [],
            },
            {
              metric: EntityInformationMatchMetric.ENTITY_STATUS,
              metric_type: RiskMetricType.TEXT,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [],
            },
          ],
        },
        {
          parameter: RiskParameterName.SAR_VOLUME,
          weight: 0.5,
          parameter_args: {
            SAR_COUNT_PAST_12_MONTHS: {
              statuses: [
                'SUBMITTING',
                'SUBMISSION_ACCEPTED',
                'SUBMISSION_SUCCESSFUL',
                'ARCHIVED',
              ],
            },
          },
          metrics: [
            {
              metric: SarVolumeMetric.SAR_COUNT_PAST_12_MONTHS,
              metric_type: RiskMetricType.NUMBER,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  content: {
                    operator: NumericComparisonOperator.GT,
                    value: 0,
                  },
                  risk: 100,
                },
              ],
            },
          ],
        },
        {
          parameter: RiskParameterName.ARTICLE_VOLUME,
          weight: 0.5,
          parameter_args: {
            ALERT_COUNT_PAST_12_MONTHS: {
              alert_types: [AlertType.TM],
              dispositions: ['TRUE_POSITIVE'],
              statuses: [InvestigationStatus.CLOSED],
            },
            CASE_COUNT_PAST_12_MONTHS: {
              dispositions: ['TRUE_POSITIVE'],
              statuses: [InvestigationStatus.CLOSED],
            },
          },
          metrics: [
            {
              metric: ArticleVolumeMetric.ALERT_COUNT_PAST_12_MONTHS,
              metric_type: RiskMetricType.NUMBER,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  content: {
                    operator: NumericComparisonOperator.GT,
                    value: 0,
                  },
                  risk: 100,
                },
              ],
            },
            {
              metric: ArticleVolumeMetric.CASE_COUNT_PAST_12_MONTHS,
              metric_type: RiskMetricType.NUMBER,
              unknown_risk: 0,
              unscored_risk: 0,
              risk_mappings: [
                {
                  method: MetricMappingMethod.NUMERIC_COMPARISON,
                  content: {
                    operator: NumericComparisonOperator.GT,
                    value: 0,
                  },
                  risk: 100,
                },
              ],
            },
          ],
        },
      ],
    },
  ];

export const NUMERIC_COMPARISON_OPERATOR_OPTIONS = Object.values({
  [NumericComparisonOperator.GT]: {
    text: 'Greater than',
    value: NumericComparisonOperator.GT,
  },
  [NumericComparisonOperator.LT]: {
    text: 'Less than',
    value: NumericComparisonOperator.LT,
  },
  [NumericComparisonOperator.EQ]: {
    text: 'Equal to',
    value: NumericComparisonOperator.EQ,
  },
  [NumericComparisonOperator.NEQ]: {
    text: 'Not equal to',
    value: NumericComparisonOperator.NEQ,
  },
  [NumericComparisonOperator.IN_RANGE]: {
    text: 'In between',
    value: NumericComparisonOperator.IN_RANGE,
  },
} satisfies {
  [k in NumericComparisonOperator]: U21SelectOptionProps<NumericComparisonOperator>;
});

// Make sure we show the ACTIVE rules
export const RULES_SELECTOR_FILTERED_STATUSES: RuleStatuses[] = [
  RuleStatuses.ACTIVE,
  RuleStatuses.ACTIVE_SHADOW,
];

export const DEFAULT_RISK_SCORES_SORT_METHOD: Omit<
  PaginationPayload,
  'limit' | 'offset'
> = {
  sort_direction: 'descending',
  sort_column: 'score',
};
